import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, X, TrendingUp, TrendingDown, Minus, Truck, FileText, ClipboardList, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { formatDateTime, formatIDR } from "@/lib/format";

const PRICE_FIELD_LABELS = {
  price_non_vat: "Harga Non PPN",
  price_ex_vat: "Harga Exc PPN",
  price_with_vat: "Harga + PPN",
  delivery_cost: "Biaya Kirim",
  unloading_fee: "Biaya Bongkar",
};

function PriceChangeRow({ field, change }) {
  const o = Number(change.old) || 0;
  const n = Number(change.new) || 0;
  const diff = Number(change.diff ?? (n - o));
  const pct = (change.pct != null) ? Number(change.pct) : (o ? (diff / o * 100) : null);
  const dir = change.direction || (diff > 0 ? "naik" : diff < 0 ? "turun" : "tetap");
  const tone = dir === "naik" ? "text-emerald-700" : dir === "turun" ? "text-red-700" : "text-stone-500";
  const Icon = dir === "naik" ? TrendingUp : dir === "turun" ? TrendingDown : Minus;
  const label = PRICE_FIELD_LABELS[field] || field;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 text-[11px] ${tone}`}>
      <Icon className="h-3 w-3" />
      <span className="font-semibold uppercase">{dir}</span>
      <span className="text-stone-500">·</span>
      <span className="text-stone-700">{label}:</span>
      <span className="font-mono">{formatIDR(o)} → {formatIDR(n)}</span>
      {diff !== 0 && (
        <span className="font-mono text-stone-700">
          ({diff > 0 ? "+" : ""}{formatIDR(diff)}{pct != null ? ` · ${diff > 0 ? "+" : ""}${pct.toFixed(2)}%` : ""})
        </span>
      )}
    </div>
  );
}

function alertIcon(a) {
  if (a.type === "low_stock") return <AlertTriangle className="h-3.5 w-3.5 text-red-600" />;
  if (a.type === "po_new") return <FileText className="h-3.5 w-3.5 text-blue-600" />;
  if (a.type === "sj_new") return <Truck className="h-3.5 w-3.5 text-emerald-600" />;
  if (a.type === "employee_request_new") return <ClipboardList className="h-3.5 w-3.5 text-indigo-600" />;
  if (a.type === "employee_request_decision") {
    const st = (a.detail || {}).status;
    if (st === "approved") return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />;
    if (st === "rejected") return <XCircle className="h-3.5 w-3.5 text-red-600" />;
    return <ClipboardList className="h-3.5 w-3.5 text-indigo-600" />;
  }
  return <Bell className="h-3.5 w-3.5 text-amber-600" />;
}

/**
 * AggregatedPriceChange — Iter v24 #3.
 * When multiple price updates happen in a 10-minute window, the backend aggregates
 * them into a single notification with detail_items[]. By default we only show the
 * summary count + collapse; clicking expands to show ALL items.
 */
function AggregatedPriceChange({ items, alertId, variant }) {
  const [expanded, setExpanded] = useState(false);
  if (!items?.length) return null;
  return (
    <div className="mt-1">
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
        className="text-[10px] uppercase tracking-wider text-terra-700 hover:underline"
        data-testid={`bell-${variant}-aggregate-toggle-${alertId}`}
      >
        {expanded ? "Sembunyikan detail" : `Lihat ${items.length} perubahan harga →`}
      </button>
      {expanded && (
        <div className="mt-2 space-y-2 max-h-72 overflow-y-auto pr-1 border-l-2 border-amber-300 pl-2" data-testid={`bell-${variant}-aggregate-list-${alertId}`}>
          {items.map((it, i) => (
            <div key={i} className="bg-amber-50/40 px-2 py-1.5 border border-amber-100">
              <div className="text-[11px] font-semibold text-stone-900 truncate">{it.material_name}{it.unit ? ` · ${it.unit}` : ""}{it.location ? ` · ${it.location}` : ""}</div>
              <div className="mt-1 space-y-0.5">
                {Object.entries(it.changes || {}).map(([f, ch]) => <PriceChangeRow key={f} field={f} change={ch} />)}
              </div>
              <div className="text-[9px] text-stone-400 font-mono mt-1">{formatDateTime(it.ts)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * AlertsBell — header bell with dropdown. Two flavors:
 *  - variant="notification" (Bell icon): PO baru, SJ baru, perubahan harga
 *  - variant="warning" (AlertTriangle icon): stok menipis, peringatan kritis
 */
export default function AlertsBell({ variant = "notification" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ notification: 0, warning: 0, total: 0 });
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  // Iter v34bc — mapping alert type -> destination route (click-to-navigate).
  const alertLink = (a) => {
    if (a.type === "employee_request_new" || a.type === "employee_request_decision") return "/employee-requests";
    return null;
  };

  const loadCounts = useCallback(async () => {
    try { const r = await api.get("/alerts/counts"); setCounts(r.data); } catch { /* ignore */ }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const r = await api.get("/alerts", { params: { category: variant } });
      setItems(r.data);
    } catch { /* ignore */ }
  }, [variant]);

  useEffect(() => {
    loadCounts();
    const id = setInterval(loadCounts, 20000);
    return () => clearInterval(id);
  }, [loadCounts]);

  // Iter v34bd — SSE real-time notifications. EventSource membuka koneksi
  // persistent ke /api/alerts/stream. Setiap event `alert` yang match variant
  // (notification/warning) langsung update counts + tambah item ke atas list
  // (jika dropdown sedang open). Auto-reconnect built-in di browser.
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    let es;
    try {
      es = new EventSource(`${base}/api/alerts/stream`, { withCredentials: true });
    } catch { return; }
    const onAlert = (e) => {
      try {
        const data = JSON.parse(e.data);
        const cat = data.category || (data.type === "low_stock" ? "warning" : "notification");
        if (cat !== variant) return;
        // Update badge counts optimistically.
        setCounts((c) => ({ ...c, [cat]: (c[cat] || 0) + 1, total: (c.total || 0) + 1 }));
        // Refresh list jika dropdown lagi open.
        setItems((prev) => {
          if (prev.some((a) => a.id === data.id)) return prev;
          return [data, ...prev];
        });
      } catch { /* ignore malformed event */ }
    };
    es.addEventListener("alert", onAlert);
    es.onerror = () => { /* Browser auto-reconnects on error. */ };
    return () => { try { es.close(); } catch { /* noop */ } };
  }, [variant]);

  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.post(`/alerts/${id}/read`);
      setItems((arr) => arr.filter((a) => a.id !== id));
      loadCounts();
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await api.post("/alerts/mark-all-read", null, { params: { category: variant } });
      setItems([]);
      loadCounts();
    } catch { /* ignore */ }
  };

  const deleteAll = async () => {
    if (!window.confirm(`Hapus SEMUA ${variant === "warning" ? "peringatan" : "notifikasi"}? Tindakan ini tidak bisa diundo.`)) return;
    try {
      await api.delete("/alerts", { params: { category: variant } });
      setItems([]);
      loadCounts();
    } catch { /* ignore */ }
  };

  const count = counts[variant] || 0;
  const Icon = variant === "warning" ? AlertTriangle : Bell;
  const tone = variant === "warning" ? "text-red-600 hover:text-red-700" : "text-stone-600 hover:text-stone-900";
  const badgeBg = variant === "warning" ? "bg-red-600" : "bg-terra-500";
  const title = variant === "warning" ? "Peringatan" : "Notifikasi";

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`relative p-1.5 transition-colors ${tone}`}
        title={title}
        data-testid={`bell-${variant}`}
      >
        <Icon className="h-4 w-4" />
        {count > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 ${badgeBg} text-white text-[9px] font-bold flex items-center justify-center rounded-full font-mono`}>
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed sm:absolute top-16 sm:top-full mt-0 sm:mt-2 inset-x-2 sm:inset-x-auto sm:right-0 sm:left-auto w-auto sm:w-[360px] max-w-none sm:max-w-[90vw] max-h-[75vh] sm:max-h-[480px] bg-white border border-stone-200 shadow-2xl z-50 flex flex-col"
          data-testid={`bell-${variant}-panel`}
        >
          <div className="px-3 py-2 border-b border-stone-200 flex items-center justify-between bg-stone-50">
            <div className="flex items-center gap-1.5">
              <Icon className={`h-3.5 w-3.5 ${variant === "warning" ? "text-red-600" : "text-stone-700"}`} />
              <span className="text-xs font-semibold text-stone-800 uppercase tracking-wider">{title}</span>
              {items.length > 0 && <span className="text-[10px] text-stone-500 font-mono">({items.length})</span>}
            </div>
            <div className="flex items-center gap-2">
              {items.length > 0 && (
                <>
                  <button onClick={markAllRead} className="text-[10px] uppercase tracking-wider text-stone-500 hover:text-terra-700" data-testid={`bell-${variant}-mark-all`}>
                    Tandai semua
                  </button>
                  <span className="text-stone-300">|</span>
                  <button onClick={deleteAll} className="text-[10px] uppercase tracking-wider text-red-600 hover:text-red-800" data-testid={`bell-${variant}-delete-all`}>
                    Hapus semua
                  </button>
                </>
              )}
              <button onClick={() => setOpen(false)} className="text-stone-500 hover:text-stone-900" data-testid={`bell-${variant}-close`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-xs text-stone-400">
                {variant === "warning" ? "Tidak ada peringatan." : "Tidak ada notifikasi baru."}
              </div>
            ) : items.map((a) => {
              const isAggregated = a.type === "price_change" && Array.isArray(a.detail_items) && a.detail_items.length > 1;
              const link = alertLink(a);
              const clickable = !!link;
              const onItemClick = () => {
                if (!clickable) return;
                markRead(a.id);
                setOpen(false);
                navigate(link);
              };
              return (
              <div
                key={a.id}
                className={`px-3 py-2.5 border-b border-stone-100 hover:bg-stone-50/60 ${clickable ? "cursor-pointer" : ""}`}
                onClick={onItemClick}
                data-testid={`bell-${variant}-item-${a.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="mt-0.5">{alertIcon(a)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-stone-900">{a.title}</div>
                    {a.type === "price_change" && !isAggregated && a.detail && (
                      <div className="mt-1 space-y-0.5">
                        {Object.entries(a.detail).map(([f, ch]) => <PriceChangeRow key={f} field={f} change={ch} />)}
                      </div>
                    )}
                    {isAggregated && (
                      <AggregatedPriceChange items={a.detail_items} alertId={a.id} variant={variant} />
                    )}
                    {a.type === "low_stock" && a.detail && (
                      <div className="text-[11px] text-stone-600 mt-0.5">
                        Stok: <span className="font-mono">{a.detail.quantity}</span> · Batas: <span className="font-mono">{a.detail.threshold}</span>
                      </div>
                    )}
                    {a.type === "po_new" && a.detail && (
                      <div className="text-[11px] text-stone-600 mt-0.5">
                        Klien: <span className="font-medium">{a.detail.client_name || "—"}</span>
                      </div>
                    )}
                    {a.type === "sj_new" && a.detail && (
                      <div className="text-[11px] text-stone-600 mt-0.5">
                        Klien: <span className="font-medium">{a.detail.client_name || "—"}</span> · Tgl: <span className="font-mono">{a.detail.date || "—"}</span>
                      </div>
                    )}
                    {a.type === "employee_request_new" && a.detail && (
                      <div className="text-[11px] text-stone-600 mt-0.5 space-y-0.5">
                        <div>Kategori: <span className="font-medium">{a.detail.category_name || "—"}</span></div>
                        <div>Urgensi: <span className={`font-semibold ${a.detail.urgency === "urgent" || a.detail.urgency === "high" ? "text-red-700" : "text-stone-700"}`}>{a.detail.urgency_text || a.detail.urgency || "—"}</span></div>
                      </div>
                    )}
                    {a.type === "employee_request_decision" && a.detail && (
                      <div className="text-[11px] text-stone-600 mt-0.5 space-y-0.5">
                        <div>Oleh: <span className="font-medium">{a.detail.reviewed_by || "—"}</span></div>
                        {a.detail.admin_note && <div>Catatan: <span className="italic">{a.detail.admin_note}</span></div>}
                      </div>
                    )}
                    <div className="text-[10px] text-stone-400 font-mono mt-1">{formatDateTime(a.ts)}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); markRead(a.id); }} className="text-stone-400 hover:text-stone-700 mt-0.5" title="Tandai dibaca" data-testid={`bell-${variant}-read-${a.id}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );})}
          </div>
        </div>
      )}
    </div>
  );
}
